import React from 'react';
import { Container, Tabs, Tab } from 'react-bootstrap';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import Select from 'react-select';

import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import InformationModal from '../../Components/InformationModal/InformationModal';
import { SapMasterApi } from '../../Services/ConciliationApiDataService';
import OficialNoMatchWorkbenchTab from './OficialNoMatchWorkbenchTab';
import EntFinNoMatchWorkbenchTab from './EntFinNoMatchWorkbenchTab';
import ConciliaRelWorkbenchTab from './ConciliaRelWorkbenchTab';
import { getStoredConciliationCompanyId, setStoredConciliationCompanyId } from './companySelectionStore';

import './ManualPostingPage.scss';

const useToken = () => {
  const { instance, accounts } = useMsal();

  return React.useCallback(async () => {
    const req = { ...loginRequest, account: accounts[0] };
    const { accessToken } = await instance.acquireTokenSilent(req);
    return accessToken;
  }, [instance, accounts]);
};

const ManualPostingPageContent = () => {
  const getToken = useToken();

  const [companyId, setCompanyId] = React.useState(() => getStoredConciliationCompanyId());
  const [companyOptions, setCompanyOptions] = React.useState([]);
  const [loadingCompanies, setLoadingCompanies] = React.useState(false);

  const [activeTab, setActiveTab] = React.useState('rel');

  const [infoModal, setInfoModal] = React.useState({ show: false, title: '', content: null });

  const showInfoModal = React.useCallback((title, content) => {
    setInfoModal({ show: true, title, content });
  }, []);

  const hideInfoModal = React.useCallback(() => {
    setInfoModal((prev) => ({ ...prev, show: false }));
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoadingCompanies(true);
        const token = await getToken();
        const companies = await SapMasterApi.getCompanies(token);
        if (cancelled) return;

        const opts = (companies ?? []).map((c) => ({
          value: c.companyId,
          label: c.companyName ?? `Company ${c.companyId}`
        }));

        setCompanyOptions(opts);
        if (companyId && !opts.some((o) => o.value === companyId)) {
          setCompanyId(null);
          setStoredConciliationCompanyId(null);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to load companies', e);
        }
      } finally {
        if (!cancelled) {
          setLoadingCompanies(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getToken, companyId]);

  const selectedCompany = React.useMemo(
    () => companyOptions.find((o) => o.value === companyId) ?? null,
    [companyOptions, companyId]
  );

  return (
    <Container fluid className="manual-posting-page h-100 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center w-100 mb-2">
        <div style={{ minWidth: 280 }}>
          <Select
            classNamePrefix="react-select"
            options={[{ value: null, label: 'Select company...' }, ...companyOptions]}
            value={selectedCompany}
            onChange={(opt) => {
              const id = opt?.value ?? null;
              setCompanyId(id);
              setStoredConciliationCompanyId(id);
            }}
            isLoading={loadingCompanies}
            placeholder="Select company..."
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
      </div>
      {!companyId && (
        <div className="alert alert-info mb-2">
          Select a company to load manual posting records.
        </div>
      )}

      <div className="manual-posting-tabs-wrap flex-grow-1 d-flex flex-column min-h-0">
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || 'oficial')}
          className="manual-posting-tabs mb-0"
          mountOnEnter
          unmountOnExit
        >
          <Tab eventKey="rel" title="Invoice + Payment" className="h-100">
            <ConciliaRelWorkbenchTab
              companyId={companyId}
              getToken={getToken}
              showInfoModal={showInfoModal}
            />
          </Tab>

          <Tab eventKey="oficial" title="Purchase Invoice" className="h-100">
            <OficialNoMatchWorkbenchTab
              companyId={companyId}
              getToken={getToken}
              showInfoModal={showInfoModal}
            />
          </Tab>

          <Tab eventKey="entfin" title="Vendor Payment" className="h-100">
            <EntFinNoMatchWorkbenchTab
              companyId={companyId}
              getToken={getToken}
              showInfoModal={showInfoModal}
            />
          </Tab>
        </Tabs>
      </div>

      <InformationModal
        show={infoModal.show}
        onHide={hideInfoModal}
        onAcceptClick={hideInfoModal}
        title={infoModal.title}
        content={infoModal.content}
      />
    </Container>
  );
};

const ManualPostingPage = () => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
    <PageLayout2 title="Manual Posting Workbench">
      <ManualPostingPageContent />
    </PageLayout2>
  </MsalAuthenticationTemplate>
);

export default ManualPostingPage;
